/**
 * L5 Map Coverage Audit — cross-reference wp_posts ↔ map.
 *
 * 4 reguły:
 *  - matched_ratio_per_seed (ile nodów seeda ma matched_wp_post_id)
 *  - ghost_seeds (severity po priority: 1→error, 2-3→warning, 4+→info)
 *  - reverse_node_suggestions (wp_posts monitor no_node + Jaccard do seed.name)
 *  - orphan_existing_posts (cluster_fit=in_scope + no match + no decyzja publish_queue)
 */

import { supabase } from "@/integrations/supabase/client";
import { tokenize, jaccardSimilarity } from "@/services/deduplicationService";
import type { AuditConfig, AuditSeverity, AuditIssue } from "@/types/thematicMaps";

const PREFIX = "[AUDIT-L5-COVER]";

export interface L5CoverageResult {
  score: number;
  max: number;
  verdict: "pass" | "warn" | "fail";
  matched_ratio_global: number;
  matched_ratio_per_seed: Array<{
    seed_id: string;
    seed_name: string;
    priority: number;
    total_nodes: number;
    matched: number;
    ratio: number;
  }>;
  ghost_seeds: Array<{
    seed_id: string;
    seed_name: string;
    priority: number;
    total_nodes: number;
    severity: AuditSeverity;
  }>;
  reverse_node_suggestions: Array<{
    wp_post_id: string;
    wp_title: string;
    wp_url: string | null;
    extracted_entity: string;
    candidates: Array<{
      seed_id: string;
      seed_name: string;
      jaccard: number;
    }>;
  }>;
  orphan_existing_posts: Array<{
    wp_post_id: string;
    wp_title: string;
    wp_url: string | null;
    extracted_entity: string | null;
    cluster_fit: string | null;
  }>;
  issues: AuditIssue[];
}

const DEFAULT_CFG = {
  jaccard_threshold: 0.3,
  top_suggestions_per_post: 3,
  max_affected_per_issue: 10,
  low_match_warning_ratio: 0.2,
};

export async function auditMapCoverage(
  mapId: string,
  websiteId: string | null,
  config?: AuditConfig,
): Promise<L5CoverageResult> {
  const maxAffected = config?.max_affected_per_issue ?? DEFAULT_CFG.max_affected_per_issue;
  const issues: AuditIssue[] = [];

  // Bez website → L5 skipowany
  if (!websiteId) {
    return {
      score: 20,
      max: 20,
      verdict: "pass",
      matched_ratio_global: 0,
      matched_ratio_per_seed: [],
      ghost_seeds: [],
      reverse_node_suggestions: [],
      orphan_existing_posts: [],
      issues: [
        {
          severity: "info",
          dimension: "coverage",
          message: "Mapa bez przypisanego website_id — L5 coverage pominięty",
          affected_slugs: [],
          suggested_fix: "Przypisz website do mapy w kroku Macro jeśli chcesz cross-ref z wpisami WP",
        },
      ],
    };
  }

  // Load seeds + nodes + wp_posts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: seedsData } = await (supabase as any)
    .from("thematic_seeds")
    .select("id, name, priority")
    .eq("map_id", mapId)
    .or("status.is.null,status.eq.active"); // M18: pomijamy pending_production/deprecated/archived
  const seeds = (seedsData || []) as Array<{ id: string; name: string; priority: number | null }>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodesData } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, seed_id, matched_wp_post_id")
    .eq("map_id", mapId);
  const nodes = (nodesData || []) as Array<{
    id: string;
    seed_id: string;
    matched_wp_post_id: string | null;
  }>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: wpData } = await (supabase as any)
    .from("wp_posts")
    .select(
      "id, title, wp_url, extracted_entity, cluster_fit, final_action, final_action_locked, final_action_reason",
    )
    .eq("website_id", websiteId);
  const wpPosts = (wpData || []) as Array<{
    id: string;
    title: string | null;
    wp_url: string | null;
    extracted_entity: string | null;
    cluster_fit: string | null;
    final_action: string | null;
    final_action_locked: boolean | null;
    final_action_reason: string | null;
  }>;

  const matchedPostIds = new Set(
    nodes.filter((n) => n.matched_wp_post_id).map((n) => n.matched_wp_post_id as string),
  );

  // ─── matched_ratio_per_seed ───
  const totalNodes = nodes.length;
  const totalMatched = nodes.filter((n) => n.matched_wp_post_id).length;
  const globalRatio = totalNodes > 0 ? totalMatched / totalNodes : 0;

  const perSeed = seeds.map((s) => {
    const seedNodes = nodes.filter((n) => n.seed_id === s.id);
    const matched = seedNodes.filter((n) => n.matched_wp_post_id).length;
    return {
      seed_id: s.id,
      seed_name: s.name,
      priority: s.priority ?? 3,
      total_nodes: seedNodes.length,
      matched,
      ratio: seedNodes.length > 0 ? Math.round((matched / seedNodes.length) * 100) / 100 : 0,
    };
  });

  // ─── ghost_seeds (0% matched + severity per priority) ───
  const ghost_seeds: L5CoverageResult["ghost_seeds"] = [];
  for (const s of perSeed) {
    if (s.total_nodes === 0 || s.matched > 0) continue;
    // 0% matched — severity per priority
    const priority = s.priority ?? 3;
    let sev: AuditSeverity;
    if (priority === 1) sev = "error";
    else if (priority === 2 || priority === 3) sev = "warning";
    else sev = "info";

    ghost_seeds.push({
      seed_id: s.seed_id,
      seed_name: s.seed_name,
      priority,
      total_nodes: s.total_nodes,
      severity: sev,
    });
  }

  if (ghost_seeds.length > 0) {
    const errors = ghost_seeds.filter((g) => g.severity === "error");
    if (errors.length > 0) {
      issues.push({
        severity: "error",
        dimension: "coverage",
        message: `${errors.length} core seeds (priority=1) bez żadnego matched wpisu WP — cały klaster do wyprodukowania`,
        affected_slugs: errors.map((g) => g.seed_name).slice(0, maxAffected),
        suggested_fix: "Uruchom Content Engine dla tych seedów (najwyższy priorytet)",
      });
    }
    const warnings = ghost_seeds.filter((g) => g.severity === "warning");
    if (warnings.length > 0) {
      issues.push({
        severity: "warning",
        dimension: "coverage",
        message: `${warnings.length} seedów priority 2-3 bez matched wpisów`,
        affected_slugs: warnings.map((g) => g.seed_name).slice(0, maxAffected),
        suggested_fix: "Rozważ produkcję CE dla tych seedów lub zmapuj ręcznie do istniejących wpisów",
      });
    }
  }

  // ─── Low match ratio global (warning) ───
  if (totalNodes > 0 && globalRatio < DEFAULT_CFG.low_match_warning_ratio) {
    issues.push({
      severity: "warning",
      dimension: "coverage",
      message: `Tylko ${(globalRatio * 100).toFixed(0)}% nodów mapy ma matched wpis WP — niskie pokrycie`,
      affected_slugs: [],
      suggested_fix: "Uruchom SRO Matcher dla większej ilości dopasowań albo planuj produkcję CE",
    });
  }

  // ─── reverse_node_suggestions (monitor no_node + Jaccard) ───
  // Wpisy które LUKA #1 Decision Engine skierowała do monitor bo brak noda.
  const monitorNoNodeCandidates = wpPosts.filter(
    (p) =>
      p.final_action === "monitor" &&
      !matchedPostIds.has(p.id) &&
      (p.final_action_reason || "").toLowerCase().includes("brak noda") &&
      p.extracted_entity,
  );

  const seedTokens = seeds.map((s) => ({ seed_id: s.id, name: s.name, tokens: tokenize(s.name) }));
  const reverse_node_suggestions: L5CoverageResult["reverse_node_suggestions"] = [];

  for (const post of monitorNoNodeCandidates.slice(0, 50)) {
    const entity = post.extracted_entity || "";
    const postTokens = tokenize(entity);
    if (postTokens.length === 0) continue;

    const scored = seedTokens
      .map((s) => ({
        seed_id: s.seed_id,
        seed_name: s.name,
        jaccard: jaccardSimilarity(postTokens, s.tokens),
      }))
      .filter((s) => s.jaccard >= DEFAULT_CFG.jaccard_threshold)
      .sort((a, b) => b.jaccard - a.jaccard)
      .slice(0, DEFAULT_CFG.top_suggestions_per_post);

    if (scored.length > 0) {
      reverse_node_suggestions.push({
        wp_post_id: post.id,
        wp_title: post.title || "(brak tytułu)",
        wp_url: post.wp_url,
        extracted_entity: entity,
        candidates: scored.map((s) => ({
          seed_id: s.seed_id,
          seed_name: s.seed_name,
          jaccard: Math.round(s.jaccard * 100) / 100,
        })),
      });
    }
  }

  if (reverse_node_suggestions.length > 0) {
    issues.push({
      severity: "info",
      dimension: "coverage",
      message: `${reverse_node_suggestions.length} wpisów WP z LUKA #1 (monitor bez noda) — propozycje reverse node generation`,
      affected_slugs: reverse_node_suggestions.slice(0, maxAffected).map((r) => r.wp_title),
      suggested_fix: "Przejrzyj listę i wygeneruj nowe nody dla pasujących wpisów (Content Engine) lub zmapuj ręcznie",
    });
  }

  // ─── orphan_existing_posts (cluster_fit=in_scope + no match + no decyzja locked) ───
  const orphanPosts = wpPosts
    .filter(
      (p) =>
        p.cluster_fit === "in_scope" &&
        !matchedPostIds.has(p.id) &&
        (!p.final_action_locked || (p.final_action !== "keep" && p.final_action !== "refurbish_url_preserve")),
    )
    .slice(0, 50);

  const orphan_existing_posts = orphanPosts.slice(0, maxAffected).map((p) => ({
    wp_post_id: p.id,
    wp_title: p.title || "(brak)",
    wp_url: p.wp_url,
    extracted_entity: p.extracted_entity,
    cluster_fit: p.cluster_fit,
  }));

  if (orphanPosts.length > 5) {
    issues.push({
      severity: "info",
      dimension: "coverage",
      message: `${orphanPosts.length} wpisów WP in_scope bez matched noda ani zaakceptowanej decyzji`,
      affected_slugs: orphan_existing_posts.map((o) => o.wp_title),
      suggested_fix: "Uruchom SRO Matcher ponownie lub przejrzyj w zakładce Decyzje panelu Istniejące wpisy",
    });
  }

  // ─── Score (0-20) ───
  let score = 20;
  if (globalRatio < 0.1) score -= 6;
  else if (globalRatio < 0.2) score -= 3;
  const errorGhosts = ghost_seeds.filter((g) => g.severity === "error").length;
  const warnGhosts = ghost_seeds.filter((g) => g.severity === "warning").length;
  score -= Math.min(errorGhosts * 3, 6);
  score -= Math.min(warnGhosts, 3);
  if (orphanPosts.length > 20) score -= 2;
  score = Math.max(0, score);

  const verdict: "pass" | "warn" | "fail" = score >= 16 ? "pass" : score >= 10 ? "warn" : "fail";

  return {
    score,
    max: 20,
    verdict,
    matched_ratio_global: Math.round(globalRatio * 100) / 100,
    matched_ratio_per_seed: perSeed,
    ghost_seeds,
    reverse_node_suggestions,
    orphan_existing_posts,
    issues,
  };
}

export { PREFIX as L5_PREFIX };
